/**
 * The sea: a plane to the horizon with analytic Gerstner normals (distance-faded against aliasing), sky reflection
 * from the sky-view LUTs, GGX sun and moon paths (widening with distance, only while the disc is above the skyline), a dark body colour, and the shared aerial perspective.
 */
import * as THREE from 'three';
import type { WorldState } from '../../world/state/types';
import { EYE, SEA_LEVEL_Y } from '../composition';
import type { Vec3 } from '../frame';
import type { LightLevels } from '../Lighting';
import { AERIAL_FRAG_APPLY, AERIAL_FRAG_PARS, AERIAL_UNIFORMS } from '../materials/fog';
import { ATMOSPHERE_GLSL } from '../sky/atmosphereShaders';
import type { SkyRenderer } from '../sky/SkyRenderer';
import { skylineAltDeg } from '../far/layout';
import { seaRoughness, wavePhases, waveSet } from './waves';

const VERT = /* glsl */ `
varying vec3 vAerialWorld;
void main() {
  vAerialWorld = (modelMatrix * vec4(position, 1.0)).xyz;
  gl_Position = projectionMatrix * viewMatrix * vec4(vAerialWorld, 1.0);
}`;

const FRAG = /* glsl */ `
${ATMOSPHERE_GLSL}
${AERIAL_FRAG_PARS}
uniform sampler2D uTransmittance;
uniform vec4 uWaveA[4];   // dir.x, dir.y, k, amplitude
uniform float uWavePhase[4];
uniform float uRough;
uniform vec3 uSunDir;
uniform vec3 uMoonDir;
uniform float uSunE;
uniform float uMoonE;
uniform float uReflect; // artistic: dark night sea, moon path does the talking
uniform float uSunVis;  // 0..1: how much of the disc is above the skyline (ridges, islands, sea horizon)
uniform float uMoonVis;

float ggx(float nh, float a) {
  float a2 = a * a;
  float d = nh * nh * (a2 - 1.0) + 1.0;
  return a2 / (PI * d * d);
}

void main() {
  vec3 toP = vAerialWorld - cameraPosition;
  float dist = length(toP);
  vec3 v = toP / dist;
  vec2 xz = vAerialWorld.xz;
  // Gerstner slope sum; fade high frequencies with distance (anti-aliasing at grazing angles)
  vec2 slope = vec2(0.0);
  float lostSlopeVar = 0.0; // slope variance faded out with distance: it becomes roughness, not a mirror
  for (int i = 0; i < 4; i++) {
    vec4 w = uWaveA[i];
    float fade = 1.0 / (1.0 + dist * w.z * 0.02);
    float th = w.z * dot(w.xy, xz) - uWavePhase[i];
    slope += w.xy * (w.z * w.w * cos(th)) * fade;
    float ka = w.z * w.w;
    lostSlopeVar += 0.5 * ka * ka * (1.0 - fade * fade);
  }
  vec3 n = normalize(vec3(-slope.x, 1.0, -slope.y));
  vec3 r = reflect(v, n);
  r.y = abs(r.y) + 0.002;
  vec2 ruv = aerialSkyUv(normalize(r));
  vec3 sky = texture2D(uAerialSky, ruv).rgb * uAerialSunI + texture2D(uAerialSkyMoon, ruv).rgb * uAerialMoonI + uAerialAirglow;
  float ndv = max(1e-3, dot(n, -v));
  // grazing views blow 1/(4·n·v) up into a flat white band: floor it for the specular term
  float specNdv = max(ndv, 0.2);
  float F = 0.02 + 0.98 * pow(1.0 - ndv, 5.0);
  // part of the far sea's faded wave slope comes back as roughness: the path widens and softens toward the horizon
  // (all of it, α² ≈ 2σ², spread a high moon's light over the whole sea as glitter; capped)
  float a = sqrt(uRough * uRough * uRough * uRough + min(0.6 * lostSlopeVar, 0.006));
  vec3 o = vec3(0.0, uAerialCamR, 0.0);
  vec3 hs = normalize(uSunDir - v);
  vec3 sunT = lightTransmittance(uTransmittance, o, uSunDir);
  vec3 spec = sunT * uSunE * uSunVis * ggx(max(0.0, dot(n, hs)), a) * F / (4.0 * specNdv);
  vec3 hm = normalize(uMoonDir - v);
  // the moon's own colour through the air, as its disc is drawn: warm low, white high
  vec3 moonT = lightTransmittance(uTransmittance, o, uMoonDir);
  spec += moonT * uMoonE * uMoonVis * ggx(max(0.0, dot(n, hm)), a) * F / (4.0 * specNdv);
  vec2 zuv = aerialSkyUv(vec3(0.0, 1.0, 0.0));
  vec3 zenith = texture2D(uAerialSky, zuv).rgb * uAerialSunI + texture2D(uAerialSkyMoon, zuv).rgb * uAerialMoonI;
  vec3 body = vec3(0.012, 0.03, 0.045) * (zenith * 0.6 + 0.02);
  gl_FragColor = vec4(mix(body, sky * uReflect, F) + spec, 1.0);
  ${AERIAL_FRAG_APPLY}
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}`;

export class Sea {
  readonly mesh: THREE.Mesh;
  private readonly uniforms: Record<string, THREE.IUniform>;

  constructor(
    sky: SkyRenderer,
    private readonly seed: number,
    private readonly toriiAzDeg: number,
  ) {
    this.uniforms = {
      ...AERIAL_UNIFORMS,
      uTransmittance: { value: sky.transmittance.texture },
      uWaveA: { value: [0, 1, 2, 3].map(() => new THREE.Vector4()) },
      uWavePhase: { value: [0, 0, 0, 0] },
      uRough: { value: 0.1 },
      uSunDir: { value: new THREE.Vector3(0, 1, 0) },
      uMoonDir: { value: new THREE.Vector3(0, -1, 0) },
      uSunE: { value: 3.3 },
      uMoonE: { value: 0 },
      uReflect: { value: 1 },
      uSunVis: { value: 1 },
      uMoonVis: { value: 0 },
    };
    this.mesh = new THREE.Mesh(
      new THREE.PlaneGeometry(80_000, 80_000),
      new THREE.ShaderMaterial({ vertexShader: VERT, fragmentShader: FRAG, uniforms: this.uniforms }),
    );
    this.mesh.name = 'sea';
    this.mesh.rotation.x = -Math.PI / 2;
    this.mesh.position.set(EYE[0], SEA_LEVEL_Y, EYE[2]);
  }

  update(state: WorldState, levels: LightLevels, sunDir: Vec3, moonDir: Vec3, cameraAzimuthDeg: number, animTime: number): void {
    const windScene = state.weather.windDirDeg - cameraAzimuthDeg;
    const waves = waveSet(state.weather.windSpeedMs, windScene);
    const phases = wavePhases(waves, animTime);
    const a = this.uniforms.uWaveA!.value as THREE.Vector4[];
    waves.forEach((w, i) => a[i]!.set(w.dir[0], w.dir[1], (2 * Math.PI) / w.wavelength, w.amplitude));
    this.uniforms.uWavePhase!.value = phases;
    this.uniforms.uRough!.value = seaRoughness(state.weather.windSpeedMs);
    (this.uniforms.uSunDir!.value as THREE.Vector3).set(...sunDir);
    (this.uniforms.uMoonDir!.value as THREE.Vector3).set(...moonDir);
    // Sun illuminance matching the dome's disc (radiance 2400·I over its solid angle); moon boosted artistically.
    this.uniforms.uSunE!.value = levels.skySun * 2400 * 6.8e-5;
    this.uniforms.uMoonE!.value = 0.03 * state.sky.moon.illuminatedFraction;
    const day = Math.min(1, Math.max(0, (state.sky.sun.altDeg + 6) / 12));
    this.uniforms.uReflect!.value = 0.35 + 0.65 * day;
    // a disc behind the ridges or islands lights no path on the water
    const vis = (azDeg: number, altDeg: number, radiusDeg: number): number => {
      const sky = skylineAltDeg(this.seed, this.toriiAzDeg, azDeg);
      const t = Math.min(1, Math.max(0, (altDeg - sky + radiusDeg) / (2 * radiusDeg)));
      return t * t * (3 - 2 * t);
    };
    this.uniforms.uSunVis!.value = vis(state.sky.sun.azDeg, state.sky.sun.altDeg, 0.27);
    this.uniforms.uMoonVis!.value = vis(state.sky.moon.azDeg, state.sky.moon.altDeg, 0.42);
  }
}
